import { cleanupUserScreensharesSaga } from 'features/screenshare/sagas/cleanupUserScreensharesSaga';
import { call, fork, put, select } from 'redux-saga/effects';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import { SignalingUserLeftPayload, User, UserId } from 'features/users/types';
import { selectActiveUserById, selectEvictableAwaitingEntryIds } from 'features/users/usersSlice';
import { signalingWaitingUserLeft } from 'features/users/actions';
import { selectIsSystemSession } from 'features/application/selectors';
import { eventBus } from 'utils/eventBus';
import { cleanupUsers } from 'features/users/thunks/cleanupUsers';
import { RTCClient } from 'utils/webrtc';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { cleanupE2eeUser } from 'features/e2ee/cleanupE2eeUser';
import { refreshEncryptionKeys } from 'features/e2ee/sagas/refreshEncryptionKeys';
import { board } from 'utils/whiteboard/BoardStateManager';
import {
  raiseHandNotificationUpdateRequested,
  userLeftNotificationTriggered,
} from 'features/notifications/actions';
import { refineSframeStatus } from 'features/e2ee/sagas/refineSframeStatus';
import { selectMainRoomId } from 'features/breakout-rooms/breakoutRoomsSlice';

export function* onUserLeftSaga(action: PayloadAction<SignalingUserLeftPayload>) {
  const { id: userId, roomId: payloadRoomId } = action.payload;

  const user: User = yield select(selectActiveUserById, userId);
  if (!user) {
    const evictableAwaitingEntryIds: UserId[] = yield select(selectEvictableAwaitingEntryIds);
    if (evictableAwaitingEntryIds.includes(userId)) {
      // a waiting id that never entered the room: the server has already removed it from
      // its waiting collection, and this userLeft is the only signal of that removal
      yield put(signalingWaitingUserLeft({ id: userId }));
    }
    // prevent possible race-condition from the backend
    return;
  }

  yield call(cleanupUserScreensharesSaga, action.payload.id);

  const isSystemSession: boolean = yield select(selectIsSystemSession);
  if (!isSystemSession) {
    yield put(
      userLeftNotificationTriggered({
        id: userId,
      })
    );

    yield call(eventBus.sendMessage, 'userLeft', { userId }, { reason: 'generic' });
  }

  const mainRoomId: string = yield select(selectMainRoomId);
  const roomId = payloadRoomId || mainRoomId;

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  // cleanup E2EE stuff
  if (e2eeEnabled) {
    yield call(refineSframeStatus);
    yield call(cleanupE2eeUser, user.id, roomId, user.e2eeId!);
  }

  yield call(RTCClient.receivingFeed.unloadFeedAudio, userId);

  const cleanupUserThunk: Action = yield call(cleanupUsers, [{ userId, roomId }]);
  yield put(cleanupUserThunk);

  yield call(board.removeRemotePointer, userId);

  if (!isSystemSession) {
    // we have to wait until the user is completely removed from the store
    if (e2eeEnabled) {
      yield fork(refreshEncryptionKeys);
    }

    // update raise hand notification
    yield put(raiseHandNotificationUpdateRequested({ reason: 'user-left' }));
  }
}
